/**
 * shapes.ts — kitchen world generators (day themes).
 *
 * Ported from `_proto_kitchen_v1.py` `make_kitchen` + `make_world`. A theme
 * fixes the context (time-of-day) and item-scatter density. Returns the
 * grid (Int8Array of length GRID*GRID, EMPTY or item-id) plus the context.
 *
 * `all_day` is the headline H1/H2 theme: mixed context world-to-world, so
 * the agent must handle context flips continuously.
 */
import {
  GRID, EMPTY, K, MORNING, EVENING, N_CTX, START_R, START_C,
  type ThemeName,
} from './constants';

export type Grid = Int8Array;
export type Rng = () => number;

export function newGrid(): Grid {
  return new Int8Array(GRID * GRID).fill(EMPTY);
}

export function cellAt(g: Grid, r: number, c: number): number {
  return g[r * GRID + c];
}

export function setCell(g: Grid, r: number, c: number, v: number): void {
  g[r * GRID + c] = v;
}

/** Scatter food items on the grid at the given density. `ctx` is kept in the
 *  signature for interface symmetry with makeWorld (which pairs a grid with
 *  its context); the scatter itself is context-invariant. */
export function makeKitchen(
  rng: Rng, ctx: number, density = 0.35,
): Grid {
  void ctx;
  const g = newGrid();
  for (let r = 0; r < GRID; r++) {
    for (let c = 0; c < GRID; c++) {
      if (r === START_R && c === START_C) continue;
      if (rng() < density) {
        g[r * GRID + c] = Math.floor(rng() * K);
      }
    }
  }
  return g;
}

export interface World {
  grid: Grid;
  ctx: number;
}

/** Build a world + context for a day theme. */
export function makeWorld(theme: ThemeName, rng: Rng): World {
  switch (theme) {
    case 'morning':
      return { grid: makeKitchen(rng, MORNING, 0.35), ctx: MORNING };
    case 'evening':
      return { grid: makeKitchen(rng, EVENING, 0.35), ctx: EVENING };
    case 'all_day': {
      const ctx = Math.floor(rng() * N_CTX);
      return { grid: makeKitchen(rng, ctx, 0.35), ctx };
    }
    default:
      throw new Error(`unknown theme: ${theme as string}`);
  }
}

/** Mulberry32 PRNG — same as reflex's shapes.ts uses (seeded, deterministic). */
export function makeRng(seed: number): Rng {
  let a = seed >>> 0;
  return function (): number {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
